import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChangePasswordScreen() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [current, setCurrent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const homeFor = (role) => ((role || "").toLowerCase() === "sales" ? "/sales" : "/app");

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (!u) { navigate("/app", { replace: true }); return; }
        setUser(u);
        // If no forced reset pending, don't trap the user here.
        if (!u.must_change_password && !u.password_reset_required) {
          navigate(homeFor(u.role), { replace: true });
          return;
        }
        setLoading(false);
      })
      .catch(() => navigate("/app", { replace: true }));
  }, [navigate]);

  const forced = !!(user && (user.must_change_password || user.password_reset_required));

  const submit = async () => {
    setError("");
    if (pw.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (!/[a-zA-Z]/.test(pw) || !/[0-9]/.test(pw)) { setError("Password must contain letters and numbers."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      const resp = await fetch(`${API}/api/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(forced ? { new_password: pw } : { current_password: current, new_password: pw }),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(d.detail || "Could not change password");
      toast.success("Password updated");
      navigate(homeFor(user.role), { replace: true });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-[#f4f6fa] flex items-center justify-center px-4" data-testid="change-password-screen">
      <Card className="w-full max-w-md border-border bg-card p-7">
        <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto mb-5" />
        <h1 className="text-xl font-semibold text-foreground">Change your password</h1>
        <p className="mt-2 text-sm text-foreground/60">
          {forced
            ? "Your account requires a new password before you can continue."
            : "Enter your current password and choose a new one."}
        </p>
        <div className="mt-5 space-y-3">
          {!forced && (
            <div>
              <div className="text-xs uppercase tracking-wide text-foreground/55 mb-1">Current password</div>
              <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" />
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-wide text-foreground/55 mb-1">New password</div>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 chars, letters + numbers" data-testid="cp-new" onKeyDown={(e) => e.key === "Enter" && submit()} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-foreground/55 mb-1">Confirm new password</div>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" onKeyDown={(e) => e.key === "Enter" && submit()} />
          </div>
          {error && <div className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded p-2" data-testid="cp-error">{error}</div>}
          <Button onClick={submit} disabled={saving} className="w-full bg-[var(--brand)] text-white" data-testid="cp-submit">
            {saving ? "Saving…" : "Update password"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
